'use client';

import { getAuthorImage } from '@/lib/authors';
import { getHelpCenterArticleSlug, splitHelpTags } from '@/lib/help-utils';
import { getFormattedReadingTime } from '@/lib/reading-time';
import { Language } from '@/lib/translations';
import Image from 'next/image';
import Link from 'next/link';
import { IHelpCenterArticle } from '../interfaces';
import HelpArticleTags from './HelpArticleTags';

function stripHtmlTags(html: string): string {
  return html.replace(/<[^>]*>/g, '').trim();
}

function getArticleContent(
  article: IHelpCenterArticle,
  language: Language
): string {
  // Combine all section content for reading time calculation
  const sections = article.help_center_article_sections || [];
  const content = sections
    .map(s =>
      language === 'en'
        ? s.help_center_article_section_content_en
        : s.help_center_article_section_content_cn
    )
    .join(' ');
  return content;
}

interface HelpArticleCardProps {
  article: IHelpCenterArticle;
  language: Language;
}

export default function HelpArticleCard({
  article,
  language,
}: HelpArticleCardProps) {
  const slug = getHelpCenterArticleSlug(article);
  const content = getArticleContent(article, language);
  const readingTime = getFormattedReadingTime(content, language);
  const author = article.help_center_article_extra_json?.author;

  return (
    <Link
      href={`/${language}/help-center/${slug}/`}
      className="card border border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-lg transition-shadow duration-200"
      style={{ padding: '1.25rem 1.25rem 0.75rem 1.25rem' }}
    >
      <div className="flex items-start justify-between mb-3">
        <span className="inline-block bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300 text-xs px-2 py-1 rounded-full capitalize">
          {language === 'en'
            ? article.help_center_category_name_en
            : article.help_center_category_name_cn}
        </span>
        <span
          className="text-xs"
          style={{ color: 'var(--color-foreground-secondary)' }}
        >
          {readingTime}
        </span>
      </div>

      <h3
        className="text-lg font-semibold mb-2 line-clamp-2 min-h-[3.5rem]"
        style={{ color: 'var(--color-foreground)' }}
      >
        {language === 'en'
          ? article.help_center_article_title_en
          : article.help_center_article_title_cn}
      </h3>

      <p
        className="text-sm mb-2 line-clamp-3 min-h-[4.5rem]"
        style={{ color: 'var(--color-foreground-secondary)' }}
      >
        {stripHtmlTags(
          language === 'en'
            ? article.help_center_article_introduction_en
            : article.help_center_article_introduction_cn
        )}
      </p>

      {/* Tags */}
      <HelpArticleTags
        tags={splitHelpTags(
          language === 'en'
            ? article.help_center_article_tags_en
            : article.help_center_article_tags_cn
        )}
        limit={3}
        className="mb-2"
      />

      {/* Author info */}
      {author && (
        <div className="flex items-center gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
          <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
            <Image
              src={getAuthorImage(author)}
              alt={author}
              width={24}
              height={24}
              className="object-cover"
            />
          </div>
          <span
            className="text-xs"
            style={{ color: 'var(--color-foreground-secondary)' }}
          >
            {author}
            {article.help_center_article_insert_time && (
              <>
                {' · '}
                {new Date(
                  article.help_center_article_insert_time.replace(' ', 'T')
                ).toLocaleDateString(language === 'en' ? 'en-US' : 'zh-TW', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </>
            )}
          </span>
        </div>
      )}
    </Link>
  );
}
